import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Grid, Typography, useTheme } from '@mui/material'
import { useUserItems } from 'api/aws/users/useUserItems'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import DeletePopover from 'components/MTB/DeletePopover'
import DeleteIconButton from 'components/ui/delete-icon-button'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Optimization } from 'types'
import { formatDateTime } from 'utils/formatDayjsDateTime'
import { useDeleteOptimization } from '../../api/aws/optimizations/useDeleteOptimization'
import EditOptimizationFields from './EditOptimizationFields'
dayjs.extend(utc)
dayjs.extend(timezone)

export type SavedOptimizationProps = {
  optimization: Optimization
  addtlNotes: string
  onUpdate: (field: keyof Optimization, value: string | number) => void
  onSave: any
  context: string
  expandedId: string | null
  setExpandedId: (id: string | null) => void
  scrollRef?: React.RefObject<HTMLDivElement>
  highlight: boolean
  setHighlight: Dispatch<SetStateAction<boolean>>
}

const SavedOptimization: React.FC<SavedOptimizationProps> = ({
  optimization,
  addtlNotes,
  onUpdate,
  onSave,
  context,
  expandedId,
  setExpandedId,
  scrollRef,
  highlight,
  setHighlight,
}) => {
  const theme = useTheme()
  const [localValue, setLocalValue] = useState(String(optimization.opts)) // Store as a string for user input
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { mutate: deleteOptimization } = useDeleteOptimization()

  const owner = useMemo(() => {
    if (isUsersLoading || !optimization?.owner) return null
    return users?.find(u => u?.user_uuid === optimization?.owner)
  }, [isUsersLoading, optimization?.owner])
  useEffect(() => {
    // Sync local state when optimization.opts changes externally
    setLocalValue(String(optimization.opts))
  }, [optimization.opts])

  const handleChange = e => {
    let value = e.target.value

    // Allow empty string for user input (so they can delete and retype)
    if (value === '') {
      setLocalValue('')
      return
    }

    // Ensure only valid numbers
    if (!/^\d+$/.test(value)) return

    let numValue = parseInt(value, 10)

    // Limit to range 0-10
    if (numValue < 0) numValue = 0
    if (numValue > 10) numValue = 10

    setLocalValue(String(numValue)) // Keep as string for controlled input
  }

  const handleBlur = () => {
    // Convert back to a number when the input loses focus
    const numValue = localValue === '' ? 0 : parseInt(localValue, 10)
    setLocalValue(String(numValue)) // Ensure field does not remain empty
    onUpdate('opts', numValue) // Send final number value
  }

  const handleSave = async () => {
    // If you maintain updated fields locally (or via parent's state), pass them:
    await onSave([{ PK: optimization?.PK, SK: optimization?.SK }], {
      ...optimization,
      opts: parseInt(localValue, 10), // use localValue for opts
      // If addtlNotes or any other fields are updated, ensure they are merged in as well
    })
    setIsEditMode(false)
  }

  const handleDelete = async () => {
    deleteOptimization(
      { optPK: optimization.PK, optSK: optimization.SK, orgId: optimization.orgId },
      {
        onSuccess: () => {
          console.log('Optimization deleted successfully')
        },
        onError: error => {
          console.error('Error deleting optimization:', error)
        },
      }
    )
  }
  const handleClickDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget as HTMLElement)
  }

  const handleConfirmDelete = async () => {
    await handleDelete()
    handleClosePopover()
  }
  const handleClosePopover = () => {
    setAnchorEl(null)
  }
  const handleDateRangeChange = (newStart: Date | null, newEnd: Date | null) => {
    if (newStart) onUpdate('startDate', newStart.toISOString())
    if (newEnd) onUpdate('endDate', newEnd.toISOString())
  }
  const cancelEdit = () => {
    setIsEditMode(false)
  }
  const startEdit = () => {
    setIsEditMode(true)
  }
  const formatCamelCase = (str: string) => {
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space before capital letters
      .replace(/^./, s => s.toUpperCase()) // capitalize first letter
  }

  const border = optimization?.module === 'SEO' ? '2px solid rgb(238, 111, 141)' : '2px solid rgb(251, 193, 30)'
  const isSeoOpt = optimization?.type === 'seo' ? true : false
  return (
    <>
      <Accordion
        expanded={expandedId === optimization.id}
        onChange={(_, isExpanded) => {
          setExpandedId(isExpanded ? optimization.id : null)

          if (isExpanded && scrollRef?.current) {
            setTimeout(() => {
              const targetEl = scrollRef.current
              const scrollContainer = targetEl?.closest('[data-scroll-container]') as HTMLElement | null
              if (targetEl && scrollContainer) {
                scrollContainer.scrollTo({
                  top: targetEl.offsetTop - 150,
                  behavior: 'smooth',
                })
              }
            }, 100)
          }

          if (isExpanded) {
            setHighlight(true)
            setTimeout(() => setHighlight(false), 800)
          }
        }}
        sx={{
          width: '100%',
          position: 'relative',
          zIndex: expandedId === optimization.id ? 1500 : 'auto', // Above backdrop (1400)
          // transition: 'transform 0.3s ease',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            borderLeft: border,

            width: '100%',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transition: 'transform 0.3s ease', // 👈 Control transform speed & easing
            },
            '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(180deg)',
            },
            // transition: 'border-color 0.3s ease',
          }}
        >
          <Grid
            container
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              px: 2,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            <Grid item xs={3.5}>
              <Typography
                sx={{
                  flex: 1,
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {optimization.name}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Avatar src={owner?.image_512 ?? ''} />
            </Grid>
            <Grid item xs={1.5}>
              <Typography
                sx={{
                  flex: 1,
                  textAlign: 'left',
                  color: renderColorByString(optimization.type || optimization.channel),
                }}
              >
                {formatCamelCase(optimization.type || optimization.channel)}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography
                sx={{
                  flex: 1,
                  textAlign: 'right',
                  color: theme.palette.text.secondary,
                }}
              >
                {formatDateTime(optimization.createdAt)}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                textAlign: 'right',
                flexDirection: 'row',
              }}
            >
              <Typography
                sx={{
                  textAlign: 'right',
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                }}
              >
                {isSeoOpt ? 'Opts' : '–'}
              </Typography>
              <Typography
                sx={{
                  flex: 0.3,
                  textAlign: 'right',
                  fontSize: isSeoOpt ? 16 : 12,
                  color: isSeoOpt ? 'text.primary' : 'text.secondary',
                }}
              >
                {isSeoOpt ? optimization?.opts : '–'}
              </Typography>
            </Grid>
            <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <DeleteIconButton hasErrors={false} onDelete={handleClickDelete} />
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails ref={scrollRef} sx={{ backgroundColor: theme.palette.background.details }}>
          {expandedId === optimization.id && (
            <EditOptimizationFields
              localValue={localValue}
              optimization={optimization}
              addtlNotes={addtlNotes}
              onDateRangeChange={handleDateRangeChange}
              onChange={handleChange}
              onBlur={handleBlur}
              onSave={handleSave}
              onUpdate={onUpdate}
              onCancelEdit={cancelEdit}
              onStartEdit={startEdit}
              isEditMode={isEditMode}
            />
          )}
        </AccordionDetails>
      </Accordion>
      <DeletePopover anchorEl={anchorEl} onClose={handleClosePopover} onConfirm={handleConfirmDelete} />
    </>
  )
}

export default SavedOptimization
